import toast from 'react-hot-toast';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';

import { PageHeader } from '../../../components/PageHeader';
import { useCarrierPortalSupplier } from '../CarrierPortalCarrierContext';
import { ITableHeader, Table } from '../../../components/table/Table';
import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { getDisplayError } from '../../../utils/get-display-error';
import { formatPhoneNumber } from '../../../utils/phonenumber';
import { LinkButton } from '../../../components/button/ButtonLink';
import { useTranslation } from '../../../contexts/translation-context';
import { captureException } from '@sentry/react';
import { deleteCarrierPortalContact } from '../models';

export const CarrierPortalContactsPage = () => {
  const { supplier, carrierId, refreshData } = useCarrierPortalSupplier();
  const { i18n } = useTranslation();

  const refresh = useCallback(() => {
    refreshData();
  }, [refreshData]);

  const tableHeaders: ITableHeader[] = [
    {
      id: 'name',
      name: i18n('carrierPortal.contacts.name'),
    },
    {
      id: 'email',
      name: i18n('carrierPortal.contacts.email'),
    },
    {
      id: 'phone',
      name: i18n('carrierPortal.contacts.phone'),
    },
    {
      id: 'function',
      name: i18n('carrierPortal.contacts.function'),
    },
    {
      id: 'language',
      name: i18n('carrierPortal.contacts.language'),
    },
    {
      id: 'delete',
      name: i18n('carrierPortal.contacts.delete'),
    },
  ];

  return (
    <div>
      <PageHeader title={i18n('carrierPortal.contacts.contacts')} />

      <div className="flex justify-end">
        <div className="flex p-4">
          <div>
            <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
              {i18n('customerPortal.contacts.newContact')}
            </LinkButton>
          </div>
        </div>
      </div>

      <Table
        idKey="id"
        headers={tableHeaders}
        data={supplier.contacts}
        mapData={(data) => {
          const notifications = [];
          if (data.shouldReceiveTransportOrders) {
            notifications.push(i18n('carrierContactNotifications.transportOrders'));
          }
          if (data.shouldReceiveSelfBillings) {
            notifications.push(i18n('carrierContactNotifications.selfBillings'));
          }
          return [
            <Link to={`${data.id}`} className="link-text">
              {data.name}
            </Link>,
            data.email ?? '-',
            data.phone ? formatPhoneNumber(data.phone, true) : '-',
            data.function ?? '-',
            data.language,
            notifications.join(', '),
            <ConfirmDialog
              triggerColor="danger"
              triggerText={<TrashIcon className="button-icon" />}
              title={i18n('carrierPortal.contacts.deleteContact')}
              submitText={i18n('carrierPortal.contacts.delete')}
              description={i18n('carrierPortal.contacts.deleteContactConfirmation', {
                name: data.name,
              })}
              onSubmit={async () => {
                try {
                  await deleteCarrierPortalContact(carrierId, data.id);
                  refresh();
                  toast.success(i18n('carrierPortal.contacts.deleteContactSuccess'));
                } catch (err) {
                  captureException(err);
                  toast.error(
                    i18n('carrierPortal.contacts.deleteContactError', {
                      error: getDisplayError(err),
                    }),
                  );
                }
              }}
            />,
          ];
        }}
      />
    </div>
  );
};
